import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { FILTER_DEFINITIONS, type ProductFilters, type ProductMetrics, REPORT_VIEWS, type ReportView } from './model';

const VISIBLE_ROWS = 25;

export type PageSection = {
  title: string;
  rows: Array<{ label: string; values: Record<string, number | null> }>;
  truncated: boolean;
};

export function productPerformancePageView(input: {
  ready: boolean;
  filters: ProductFilters;
  options: Record<string, Array<string>>;
  period: string;
  summary: ProductMetrics | null;
  sections: Array<PageSection>;
}): PageView {
  const { filters, options } = input;
  const filterSchema: Record<string, PageField> = {
    reportView: { type: 'enum', values: REPORT_VIEWS.map((view) => view.value) },
    from: { type: 'date' },
    to: { type: 'date' },
    ...Object.fromEntries(
      FILTER_DEFINITIONS.map(({ key }) => [key, { type: 'enum', values: [null, ...(options[key] ?? [])] }]),
    ),
  };
  return {
    title: 'Product Performance',
    ready: input.ready,
    dataMode: 'live reporting.sales',
    filters: {
      reportView: filters.reportView,
      from: filters.from,
      to: filters.to,
      ...Object.fromEntries(
        FILTER_DEFINITIONS.map(({ key }) => [
          key,
          filters.selections[key].length === 1 ? filters.selections[key][0] : null,
        ]),
      ),
    },
    filterSchema,
    activeSelections: Object.fromEntries(
      FILTER_DEFINITIONS.filter(({ key }) => filters.selections[key].length).map(({ key }) => [
        key,
        filters.selections[key],
      ]),
    ),
    period: input.period,
    summary: input.summary,
    lowMarginRange:
      filters.reportView === 'low-margin' ? { min: filters.minMargin, max: filters.maxMargin } : undefined,
    visibleSections: input.sections.map((section) => ({
      title: section.title,
      totalRows: section.rows.length,
      rows: section.rows.slice(0, VISIBLE_ROWS).map((row) => ({ label: row.label, ...row.values })),
      truncated: section.truncated || section.rows.length > VISIBLE_ROWS,
    })),
    definitions: {
      source: 'lux-analytics-prod.reporting.sales at the Tableau Sales Model grain, queried live; rebuilt daily.',
      dates:
        'Sydney transaction dates; from and to are inclusive. Product and Margin Performance default to the last 30 complete days; Low Margin to the last 7 days including today; Weekly Trends to the last 8 complete Monday weeks and never the current week. Period Comparisons is fixed to the current month to yesterday and the three months before it.',
      products:
        'Product starts on all products on Product Performance and Period Comparisons, and on the saved Tableau product list on Margin Performance, Low Margin and Weekly Trends. null selects every product.',
      measures:
        'Sales sums gross_sales_aud; margin sums margin_adjusted_aud; margin % divides period sums; orders are distinct orders.',
      missing: 'Unknown is not zero. Sums skip missing values.',
      filters:
        'Each filter accepts one value or null for all values here. activeSelections lists every value currently selected.',
    },
  };
}

/** Applies a validated askRichard edit: one value per filter, or null for every value. */
export function editFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: ProductFilters,
): Partial<ProductFilters> {
  validatePageFilters(changes, view.filterSchema);
  const next: Partial<ProductFilters> = {};
  if ('reportView' in changes) {
    next.reportView = changes.reportView as ReportView;
  }
  if ('from' in changes) {
    next.from = changes.from as string;
  }
  if ('to' in changes) {
    next.to = changes.to as string;
  }
  const selections = { ...filters.selections };
  let changed = false;
  for (const { key } of FILTER_DEFINITIONS) {
    if (!(key in changes)) {
      continue;
    }
    const value = changes[key] as string | null;
    selections[key] = value === null ? [] : [value];
    changed = true;
  }
  if (changed) {
    next.selections = selections;
  }
  return next;
}
